/**
 * Sample Record Create Page
 * Form for creating a new sample record.
 */
import { useState } from 'react';
import { useLocation } from 'wouter';
import { useSampleRecordCreate } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { canWriteSampleRecord } from '@/lib/labPermissions';

export default function SampleRecordCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { create: createRecord, loading: createLoading } = useSampleRecordCreate();

  const [formData, setFormData] = useState({
    customerId: '',
    cellBatchId: '',
    sampleNo: '',
    sampleType: '',
    collectedAmount: '',
    collectedUnit: 'ml',
  });

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canWriteSampleRecord(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有创建样本记录的权限</p>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customerId || !formData.sampleNo || !formData.sampleType) {
      toast.error('请填写必填字段');
      return;
    }

    try {
      const created = await createRecord({
        customerId: formData.customerId,
        cellBatchId: formData.cellBatchId || undefined,
        sampleNo: formData.sampleNo,
        sampleType: formData.sampleType,
        collectedAmount: formData.collectedAmount || undefined,
        collectedUnit: formData.collectedUnit,
      });
      if (created) {
        toast.success('样本记录已创建');
        navigate(`/lab/sample-records/${created.id}`);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '创建样本记录失败';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">创建样本记录</h1>
        <Button variant="outline" onClick={() => navigate('/lab/sample-records')}>
          返回列表
        </Button>
      </div>

      <Card className="max-w-2xl p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm font-semibold">
              客户 ID <span className="text-red-500">*</span>
            </label>
            <Input
              required
              value={formData.customerId}
              onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
              placeholder="输入客户 ID"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">细胞批次 ID</label>
            <Input
              value={formData.cellBatchId}
              onChange={(e) => setFormData({ ...formData, cellBatchId: e.target.value })}
              placeholder="输入细胞批次 ID（可选）"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">
              样本编号 <span className="text-red-500">*</span>
            </label>
            <Input
              required
              value={formData.sampleNo}
              onChange={(e) => setFormData({ ...formData, sampleNo: e.target.value })}
              placeholder="输入样本编号"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">
              样本类型 <span className="text-red-500">*</span>
            </label>
            <Input
              required
              value={formData.sampleType}
              onChange={(e) => setFormData({ ...formData, sampleType: e.target.value })}
              placeholder="输入样本类型"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">采集量</label>
              <Input
                type="number"
                value={formData.collectedAmount}
                onChange={(e) =>
                  setFormData({ ...formData, collectedAmount: e.target.value })
                }
                placeholder="输入采集量"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-semibold">单位</label>
              <Input
                value={formData.collectedUnit}
                onChange={(e) =>
                  setFormData({ ...formData, collectedUnit: e.target.value })
                }
                placeholder="输入单位"
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={createLoading}>
              {createLoading ? '创建中...' : '创建'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/lab/sample-records')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
